import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Cancel as CancelIcon, Dashboard as DashboardIcon } from '@mui/icons-material';
import { UI_TOKENS } from '../constants/uiTokens';

type InstanceFormData = {
  name: string;
  environment: string;
  description: string;
};

type InstanceEditorDialogProps = {
  open: boolean;
  loading: boolean;
  isEditing: boolean;
  selectedAppName?: string;
  formData: InstanceFormData;
  onFormDataChange: (next: InstanceFormData) => void;
  onClose: () => void;
  onSubmit: () => void;
};

const InstanceEditorDialog: React.FC<InstanceEditorDialogProps> = ({
  open,
  loading,
  isEditing,
  selectedAppName,
  formData,
  onFormDataChange,
  onClose,
  onSubmit,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon sx={{ color: 'primary.main', fontSize: 18 }} />
            <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
              {isEditing ? 'Edit Instance' : 'Add New Instance'}
            </Typography>
          </Box>
          {selectedAppName && (
            <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary', mt: 0.25, ml: 3.5 }}>
              for {selectedAppName} - Owners inherited from application
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CancelIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              fullWidth
              label="Instance Name"
              value={formData.name}
              onChange={(e) => onFormDataChange({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., Production Instance"
              sx={{
                '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm },
                '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl
              size="small"
              fullWidth
              required
              sx={{
                '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm },
                '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm },
              }}
            >
              <InputLabel>Environment</InputLabel>
              <Select
                value={formData.environment}
                onChange={(e) => onFormDataChange({ ...formData, environment: e.target.value })}
                label="Environment"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        fontSize: UI_TOKENS.text.bodySm,
                        minHeight: UI_TOKENS.menu.itemMinHeight,
                        py: UI_TOKENS.menu.itemPaddingY,
                      },
                    },
                  },
                }}
              >
                <MenuItem value="Production">Production</MenuItem>
                <MenuItem value="Staging">Staging</MenuItem>
                <MenuItem value="Development">Development</MenuItem>
                <MenuItem value="UAT">UAT</MenuItem>
                <MenuItem value="Testing">Testing</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => onFormDataChange({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              placeholder="Describe the purpose of this instance..."
              sx={{
                '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm },
                '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={onClose} sx={{ fontSize: UI_TOKENS.text.bodySm }}>
          Cancel
        </Button>
        <Button
          size="small"
          onClick={onSubmit}
          variant="contained"
          disabled={loading || !formData.name || !formData.environment}
          sx={{ fontSize: UI_TOKENS.text.bodySm }}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : isEditing ? 'Update Instance' : 'Create Instance'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstanceEditorDialog;
